import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";

import {
  collection,
  onSnapshot,
  query,
  where,
  doc,
} from "firebase/firestore";

import { db } from "../firebase";

export default function Cart() {
  const navigate = useNavigate();
  const { cart, updateQty, removeItem } = useCart();

  const { userProfile } = useAuth();

  const [deliveryPersons, setDeliveryPersons] = useState([]);
  const [deliveryLoading, setDeliveryLoading] = useState(true);
  const [selectedDeliveryPerson, setSelectedDeliveryPerson] =
    useState(null);

    const [productStocks, setProductStocks] = useState({});

  const [now, setNow] = useState(Date.now());

  // =========================================================
// DISCOUNT
// =========================================================

const [discountApplied, setDiscountApplied] = useState(false);

  /* =========================================================
     LOAD AVAILABLE DELIVERY PERSONS
  ========================================================= */

  useEffect(() => {
    const q = query(
      collection(db, "deliveryPersons"),
      where("available", "==", true)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const persons = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setDeliveryPersons(persons);
        setDeliveryLoading(false);
      },
      (error) => {
        console.error(
          "Delivery persons listener error:",
          error
        );

        setDeliveryPersons([]);
        setDeliveryLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  /* =========================================================
   LIVE PRODUCT STOCK
========================================================= */

useEffect(() => {

  const unsubscribers = [];

  /*
   * Only shop products need stock tracking.
   */
  const shopProducts = cart.filter(
    (item) =>
      item.type === "shop" ||
      item.shopId
  );

  /*
   * Remove duplicate product IDs.
   */
  const productIds = [
    ...new Set(
      shopProducts
        .map(
          (item) =>
            item.product_id ||
            item.id ||
            ""
        )
        .filter(Boolean)
    ),
  ];

  /*
   * No products to watch.
   */
  if (!productIds.length) {
    setProductStocks({});
    return;
  }

  productIds.forEach((productId) => {

    const productRef = doc(
      db,
      "shopproducts",
      productId
    );

    const unsubscribe = onSnapshot(
      productRef,
      (snapshot) => {

        if (!snapshot.exists()) {

          setProductStocks((prev) => ({
            ...prev,
            [productId]: 0,
          }));

          return;
        }

        const data = snapshot.data();

        const stock = Number(
          data.stock ?? 0
        );

        setProductStocks((prev) => ({
          ...prev,
          [productId]:
            Number.isFinite(stock)
              ? Math.max(0, Math.floor(stock))
              : 0,
        }));

      },
      (error) => {

        console.error(
          `Stock listener error for ${productId}:`,
          error
        );

      }
    );

    unsubscribers.push(
      unsubscribe
    );

  });

  return () => {

    unsubscribers.forEach(
      (unsubscribe) =>
        unsubscribe()
    );

  };

}, [cart]);

  /* =========================================================
     LIVE CLOCK
     ========================================================= */

  useEffect(() => {
    const timer = setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  /* =========================================================
     GET EXPIRY TIME
  ========================================================= */

  const getExpiryMillis = (person) => {
    if (!person?.availabilityExpiresAt) {
      return null;
    }

    /* Firestore Timestamp */
    if (
      typeof person.availabilityExpiresAt.toMillis ===
      "function"
    ) {
      return person.availabilityExpiresAt.toMillis();
    }

    /* JavaScript Date */
    if (
      person.availabilityExpiresAt instanceof Date
    ) {
      return person.availabilityExpiresAt.getTime();
    }

    /* String / number fallback */
    const parsed = new Date(
      person.availabilityExpiresAt
    ).getTime();

    return Number.isNaN(parsed)
      ? null
      : parsed;
  };

  /* =========================================================
   FILTER CUSTOMER-VISIBLE DELIVERY PERSONS

   RULE:
   Delivery person must belong to the
   SAME AREA as the customer.
========================================================= */

const customerArea =
  (userProfile?.area || "")
    .trim()
    .toLowerCase();

/* =========================================================
   FIND DELIVERY AREA + CHARGE FOR CUSTOMER
========================================================= */

const getMatchingDeliveryArea = (person) => {
  if (!customerArea) {
    return null;
  }

  /*
   * NEW SYSTEM:
   * Check all deliveryAreas added by the delivery person.
   */
  if (
    Array.isArray(person.deliveryAreas) &&
    person.deliveryAreas.length > 0
  ) {
    const match = person.deliveryAreas.find(
      (item) =>
        (item.area || "")
          .trim()
          .toLowerCase() === customerArea
    );

    if (match) {
      return {
        area: match.area,
        charge: Number(match.charge) || 0,
      };
    }

    return null;
  }

  /*
   * OLD DATA FALLBACK:
   * Keep existing delivery persons working.
   */
  const oldArea =
    (
      person.area ||
      person.serviceArea ||
      ""
    )
      .trim()
      .toLowerCase();

  if (oldArea === customerArea) {
    return {
      area:
        person.area ||
        person.serviceArea ||
        "",
      charge:
        Number(person.deliveryCharge) || 0,
    };
  }

  return null;
};

const availableDeliveryPersons =
  deliveryPersons.filter((person) => {

    /* -----------------------------------------
       APPROVED
    ----------------------------------------- */

    const approved =
      person.applicationStatus === "APPROVED" ||
      person.verified === true;

    if (!approved) {
      return false;
    }

    /* -----------------------------------------
       AVAILABLE
    ----------------------------------------- */

    if (person.available !== true) {
      return false;
    }

    /* -----------------------------------------
   CUSTOMER AREA MATCH
----------------------------------------- */

if (!customerArea) {
  return false;
}

const matchingDeliveryArea =
  getMatchingDeliveryArea(person);

/*
 * No matching delivery area = don't show
 * this delivery person.
 */

if (!matchingDeliveryArea) {
  return false;
}

    /* -----------------------------------------
       AVAILABILITY EXPIRY
    ----------------------------------------- */

    const expiry =
      getExpiryMillis(person);

    if (!expiry) {
      return false;
    }

    if (expiry <= now) {
      return false;
    }

    return true;
  });

  /* =========================================================
     REMOVE SELECTION IF PERSON BECOMES UNAVAILABLE
  ========================================================= */

  useEffect(() => {
    if (!selectedDeliveryPerson) {
      return;
    }

    const stillAvailable =
      availableDeliveryPersons.some(
        (person) =>
          person.id ===
          selectedDeliveryPerson.id
      );

    if (!stillAvailable) {
      setSelectedDeliveryPerson(null);

      sessionStorage.removeItem(
        "selectedDeliveryPerson"
      );
    }
  }, [
    availableDeliveryPersons,
    selectedDeliveryPerson,
  ]);

  /* =========================================================
     SELECT DELIVERY PERSON
  ========================================================= */

  const selectDeliveryPerson = (person) => {
    setSelectedDeliveryPerson(person);
  };

  /* =========================================================
     PROCEED TO CHECKOUT
  ========================================================= */

  const proceedToCheckout = () => {
  if (!selectedDeliveryPerson) {
    alert(
      "Please select a delivery person before checkout."
    );

    return;
  }

  /*
   * Delivery charge is stored in the
   * deliveryPersons Firebase document.
   */
  const matchingDeliveryArea =
  getMatchingDeliveryArea(
    selectedDeliveryPerson
  );

const deliveryCharge =
  matchingDeliveryArea
    ? Number(matchingDeliveryArea.charge) || 0
    : 0;

  const deliveryPerson = {
    uid:
      selectedDeliveryPerson.uid ||
      selectedDeliveryPerson.id,

    name:
      selectedDeliveryPerson.name ||
      selectedDeliveryPerson.driverName ||
      "Delivery Person",

    mobile:
      selectedDeliveryPerson.mobile || "",

    vehicleType:
      selectedDeliveryPerson.vehicleType ||
      selectedDeliveryPerson.autoType ||
      "",

    vehicleNumber:
      selectedDeliveryPerson.vehicleNumber ||
      selectedDeliveryPerson.autoNumber ||
      "",

    area:
  matchingDeliveryArea?.area ||
  selectedDeliveryPerson.area ||
  selectedDeliveryPerson.serviceArea ||
  "",

    rating:
      selectedDeliveryPerson.rating ?? 0,

      photo:
    selectedDeliveryPerson.photo ||
    selectedDeliveryPerson.photoURL ||
    "",

  deliveryCharge: deliveryCharge,
  };

 sessionStorage.setItem(
  "selectedDeliveryPerson",
  JSON.stringify(deliveryPerson)
);

// Save discount details for Checkout page
sessionStorage.setItem(
  "cartDiscount",
  JSON.stringify({
    applied: discountApplied,
    percentage: 1,
    amount: discountAmount
  })
);

navigate("/checkout");

  navigate("/checkout");
};

 
/* =========================================================
   CART TOTAL + DISCOUNT
========================================================= */

const subtotal = cart.reduce(
  (sum, item) =>
    sum +
    Number(item.price) *
    Number(item.qty),
  0
);

const selectedDeliveryArea =
  selectedDeliveryPerson
    ? getMatchingDeliveryArea(
        selectedDeliveryPerson
      )
    : null;

const deliveryCharge =
  selectedDeliveryArea
    ? Number(selectedDeliveryArea.charge) || 0
    : 0;

// 1% discount on product subtotal
const discountAmount = discountApplied
  ? Number((subtotal * 0.01).toFixed(2))
  : 0;

const totalAmount =
  subtotal + deliveryCharge - discountAmount;

  /* =========================================================
     EMPTY CART
  ========================================================= */

  if (cart.length === 0) {
    sessionStorage.removeItem(
      "selectedDeliveryPerson"
    );

    return (
      <div style={styles.empty}>
        <div style={styles.emptyIcon}>
          🛒
        </div>

        <h3>Your cart is empty</h3>
      </div>
    );
  }

  /* =========================================================
     REMAINING AVAILABILITY TIME
  ========================================================= */

  const getRemainingTime = (person) => {
    const expiry =
      getExpiryMillis(person);

    if (!expiry) {
      return "";
    }

    const remaining = Math.max(
      0,
      expiry - now
    );

    const minutes =
      Math.floor(
        remaining / 60000
      );

    const seconds =
      Math.floor(
        (remaining % 60000) / 1000
      );

    return `${minutes
      .toString()
      .padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;
  };

  /* =========================================================
   GET CURRENT STOCK
========================================================= */

const getProductStock = (item) => {

  const productId =
    item.product_id ||
    item.id ||
    "";

  /*
   * Shop product:
   * use live Firestore stock.
   */
  if (
    item.type === "shop" ||
    item.shopId
  ) {

    if (
      Object.prototype.hasOwnProperty.call(
        productStocks,
        productId
      )
    ) {

      return productStocks[
        productId
      ];

    }

    /*
     * While Firestore is loading,
     * fall back to cart stock if available.
     */
    const fallback =
      Number(item.stock);

    return Number.isFinite(fallback)
      ? fallback
      : null;
  }

  /*
   * Non-shop items.
   */
  const stock =
    Number(item.stock);

  return Number.isFinite(stock)
    ? stock
    : null;
};

/* =========================================================
   CHECK CART STOCK
========================================================= */

const hasStockIssue = cart.some(
  (item) => {

    const stock =
      getProductStock(item);

    /*
     * If stock is still loading,
     * don't block the cart.
     */
    if (stock === null) {
      return false;
    }

    const qty =
      Number(item.qty || 0);

    return qty > stock;

  }
);

  /* =========================================================
     UI
  ========================================================= */

  return (
    <div style={styles.page}>
      <div style={styles.scroll}>

        {/* =================================================
            CART
        ================================================= */}

        <h3 style={styles.title}>
          My Cart
        </h3>

        {cart.map((item) => (
          <div
            key={item.key}
            style={styles.item}
          >

            <div style={{ flex: 1 }}>

              <div style={styles.name}>
                {item.name}
              </div>

              {item.type === "shop" && (
                <div
                  style={{
                    fontSize: 12,
                    color: "#2563eb",
                    marginTop: 2,
                    fontWeight: 600,
                  }}
                >
                  🏪 {item.shopName}
                </div>
              )}

              <div style={styles.price}>
                ₹{item.price}
              </div>

              {/* STOCK AVAILABILITY */}

{(() => {

  const stock =
    getProductStock(item);

  const qty =
    Number(item.qty || 0);

  if (stock === null) {

    return (
      <div
        style={
          styles.stockLoading
        }
      >
        Checking stock...
      </div>
    );

  }

  if (stock <= 0) {

    return (
      <div
        style={
          styles.stockOut
        }
      >
        🔴 Out of stock
      </div>
    );

  }

  if (qty > stock) {

    return (
      <div
        style={
          styles.stockWarning
        }
      >
        ⚠ Only {stock} available
      </div>
    );

  }

  if (stock <= 3) {

    return (
      <div
        style={
          styles.stockLow
        }
      >
        ⚠ Only {stock} left
      </div>
    );

  }

  return (
    <div
      style={
        styles.stockAvailable
      }
    >
      🟢 Available: {stock}
    </div>
  );

})()}

            </div>

            {/* QUANTITY */}

            <div style={styles.qty}>

              <button
                onClick={() =>
                  updateQty(
                    item.key,
                    item.qty - 1
                  )
                }
                style={styles.qtyBtn}
              >
                −
              </button>

              <span style={styles.qtyText}>
                {item.qty}
              </span>

              <button
  onClick={() => {

    const stock =
      getProductStock(item);

    if (
      stock !== null &&
      Number(item.qty) >= stock
    ) {

      alert(
        `Only ${stock} available.`
      );

      return;

    }

    updateQty(
      item.key,
      item.qty + 1
    );

  }}
  disabled={
    (() => {

      const stock =
        getProductStock(item);

      return (
        stock !== null &&
        Number(item.qty) >= stock
      );

    })()
  }
  style={{
    ...styles.qtyBtn,

    opacity:
      (() => {

        const stock =
          getProductStock(item);

        return (
          stock !== null &&
          Number(item.qty) >= stock
        )
          ? 0.4
          : 1;

      })(),

    cursor:
      (() => {

        const stock =
          getProductStock(item);

        return (
          stock !== null &&
          Number(item.qty) >= stock
        )
          ? "not-allowed"
          : "pointer";

      })(),
  }}
>
  +
</button>

            </div>

            {/* REMOVE */}

            <button
              style={styles.remove}
              onClick={() =>
                removeItem(item.key)
              }
            >
              ✕
            </button>

          </div>
        ))}

        {/* =================================================
            DELIVERY PERSON SELECTION
        ================================================= */}

        <div style={styles.deliverySection}>

          <div style={styles.deliveryHeader}>

            <div>

              <div style={styles.deliveryTitle}>
                Choose Delivery Person
              </div>

              <div style={styles.deliverySubtitle}>
                Select an available delivery person
                for this order.
              </div>

            </div>

            <div style={styles.deliveryIcon}>
              🚚
            </div>

          </div>

          {/* LOADING */}

          {deliveryLoading ? (

            <div style={styles.deliveryLoading}>

              <div style={styles.spinner}>
                ⏳
              </div>

              <span>
                Finding available delivery persons...
              </span>

            </div>

          ) : availableDeliveryPersons.length === 0 ? (

            /* =================================================
               NO AVAILABLE PERSON
            ================================================= */

            <div style={styles.noDelivery}>

              <div style={styles.noDeliveryIcon}>
                🛵
              </div>

              <strong>
                No delivery person available
              </strong>

              <p>
                Please try again in a few minutes.
                Available delivery persons will appear
                here automatically.
              </p>

            </div>

          ) : (

            /* =================================================
               DELIVERY PERSON LIST
            ================================================= */

            <div style={styles.personList}>

              {availableDeliveryPersons.map(
                (person) => {

                  const selected =
                    selectedDeliveryPerson?.id ===
                    person.id;

                    const matchingDeliveryArea =
  getMatchingDeliveryArea(person);

                  return (
                    <button
                      key={person.id}
                      onClick={() =>
                        selectDeliveryPerson(person)
                      }
                      style={{
                        ...styles.personCard,

                        ...(selected
                          ? styles.personCardSelected
                          : {}),
                      }}
                    >

                      {/* AVATAR */}

<div style={styles.personAvatar}>
  {person.photo || person.photoURL ? (
    <img
      src={person.photo || person.photoURL}
      alt={person.name || "Delivery Person"}
      style={styles.personPhoto}
    />
  ) : (
    <span>🚚</span>
  )}
</div>

                      {/* DETAILS */}

                      <div
                        style={{
                          flex: 1,
                          textAlign: "left",
                        }}
                      >

                        <div style={styles.personName}>
                          {person.name ||
                            person.driverName ||
                            "Delivery Person"}
                        </div>

                        <div style={styles.personDetails}>

                          {(person.vehicleType ||
                            person.autoType) && (
                            <span>
                              🛵{" "}
                              {person.vehicleType ||
                                person.autoType}
                            </span>
                          )}

                          {matchingDeliveryArea?.area && (
  <span>
    📍 {matchingDeliveryArea.area}
  </span>
)}

                        </div>

                        <div style={styles.personAvailability}>
  🟢 Available for{" "}
  <strong>
    {getRemainingTime(person)}
  </strong>
</div>

<div style={styles.personCharge}>
  💰 Delivery Charge: ₹
  {matchingDeliveryArea
    ? matchingDeliveryArea.charge
    : 0}
</div>

                      </div>

                      {/* SELECTION */}

                      <div
                        style={
                          styles.selectionCircle
                        }
                      >
                        {selected && (
                          <span>✓</span>
                        )}
                      </div>

                    </button>
                  );
                }
              )}

            </div>

          )}

          {/* =================================================
              SELECTED PERSON
          ================================================= */}

          {selectedDeliveryPerson && (

            <div style={styles.selectedBox}>

              <div style={styles.selectedCheck}>
                ✓
              </div>

              <div>

                <div style={styles.selectedTitle}>
                  Delivery Person Selected
                </div>

                <div style={styles.selectedName}>
  {selectedDeliveryPerson.name ||
    selectedDeliveryPerson.driverName ||
    "Delivery Person"}
</div>

<div style={styles.selectedCharge}>
  💰 Delivery Charge: ₹
  {selectedDeliveryArea
    ? selectedDeliveryArea.charge
    : 0}
</div>

              </div>

            </div>

          )}

        </div>

        {/* =================================================
            SUMMARY
        ================================================= */}

      <div style={styles.summary}>

  <div style={styles.summaryRow}>
    <span>
      Subtotal
    </span>

    <span>
      ₹{subtotal.toFixed(2)}
    </span>
  </div>

  {discountApplied && (
    <div style={styles.discountRow}>
      <span>
        Discount (1%)
      </span>

      <span>
        −₹{discountAmount.toFixed(2)}
      </span>
    </div>
  )}

  <div style={styles.summaryRow}>
    <span>
      Delivery Charge
    </span>

    <span>
      ₹{deliveryCharge}
    </span>
  </div>

  <div style={styles.summaryTotal}>
    <span>
      Total
    </span>

    <span>
      ₹{totalAmount.toFixed(2)}
    </span>
  </div>

</div>

     {/* =================================================
    DISCOUNT
================================================= */}

{discountApplied && (
  <div style={styles.discountAppliedBox}>
    🎉 1% Discount Applied!
  </div>
)}

<button
  style={{
    ...styles.discountBtn,
    opacity: discountApplied ? 0.6 : 1,
    cursor: discountApplied
      ? "not-allowed"
      : "pointer",
  }}
  disabled={discountApplied}
  onClick={() => {
    setDiscountApplied(true);
  }}
>
  {discountApplied
    ? "✓ Discount Applied"
    : "🎁 Apply Discount (1%)"}
</button>

{/* =================================================
    CHECKOUT
================================================= */}

<button
  style={{
    ...styles.checkoutBtn,

    opacity:
      selectedDeliveryPerson &&
      !hasStockIssue
        ? 1
        : 0.5,

    cursor:
      selectedDeliveryPerson &&
      !hasStockIssue
        ? "pointer"
        : "not-allowed",
  }}
  disabled={
    !selectedDeliveryPerson ||
    hasStockIssue
  }
  onClick={proceedToCheckout}
>
  {hasStockIssue
    ? "Update Cart Quantity"
    : selectedDeliveryPerson
      ? "Proceed to Checkout"
      : "Select Delivery Person"}
</button>

        {/* =================================================
    SURPRISE GIFT MESSAGE
================================================= */}

<div style={styles.surpriseGift}>
  🎁 <span>Click Apply discount button to get 1% discount on order value!</span>
</div>

      </div>
    </div>
  );
}

/* =========================================================
   STYLES
========================================================= */

const styles = {

  page: {
    height: "100dvh",
    overflow: "hidden",
    background: "#f9fafb",
  },

  scroll: {
    height: "100%",
    overflowY: "auto",
    padding: "16px",
    paddingTop: "70px",
    paddingBottom: "90px",
    boxSizing: "border-box",
  },

  title: {
    marginBottom: 16,
  },

  item: {
    background: "#ffffff",
    borderRadius: 12,
    padding: 12,
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: 10,
  },

  name: {
    fontWeight: 600,
    fontSize: 14,
  },

  price: {
    fontSize: 13,
    color: "#555",
    marginTop: 2,
  },

  qty: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    background: "#eef2ff",
    padding: "4px",
    borderRadius: 999,
    minWidth: 100,
  },

  qtyBtn: {
    width: 30,
    height: 30,
    borderRadius: "50%",
    border: "none",
    background: "#2563eb",
    color: "#fff",
    fontSize: 16,
    fontWeight: "bold",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  },

  qtyText: {
    minWidth: 24,
    textAlign: "center",
    fontWeight: 700,
    fontSize: 15,
    color: "#111827",
  },

  remove: {
    background: "transparent",
    border: "none",
    fontSize: 18,
    color: "#ef4444",
    cursor: "pointer",
  },

  /* =====================================================
     DELIVERY
  ===================================================== */

  deliverySection: {
    marginTop: 24,
    marginBottom: 20,
  },

  deliveryHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
  },

  deliveryTitle: {
    fontSize: 17,
    fontWeight: 700,
    color: "#111827",
  },

  deliverySubtitle: {
    fontSize: 12,
    color: "#6b7280",
    marginTop: 3,
  },

  deliveryIcon: {
    fontSize: 28,
  },

  deliveryLoading: {
    background: "#ffffff",
    borderRadius: 12,
    padding: 18,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    color: "#6b7280",
    fontSize: 13,
  },

  spinner: {
    fontSize: 18,
  },

  noDelivery: {
    background: "#ffffff",
    borderRadius: 14,
    padding: 22,
    textAlign: "center",
    border: "1px solid #e5e7eb",
  },

  noDeliveryIcon: {
    fontSize: 32,
    marginBottom: 8,
  },

  personList: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },

  personCard: {
    width: "100%",
    background: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: 14,
    padding: 12,
    display: "flex",
    alignItems: "center",
    gap: 12,
    cursor: "pointer",
    textAlign: "left",
  },

  personCardSelected: {
    border: "2px solid #2563eb",
    background: "#eff6ff",
  },

  personAvatar: {
  width: 44,
  height: 44,
  borderRadius: "50%",
  background: "#eef2ff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 23,
  flexShrink: 0,
  overflow: "hidden",
},

personPhoto: {
  width: "100%",
  height: "100%",
  borderRadius: "50%",
  objectFit: "cover",
  display: "block",
},

  personName: {
    fontSize: 15,
    fontWeight: 700,
    color: "#111827",
  },

  personDetails: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    marginTop: 4,
    fontSize: 12,
    color: "#6b7280",
  },

  personAvailability: {
    marginTop: 5,
    fontSize: 11,
    color: "#16a34a",
  },

  selectionCircle: {
    width: 25,
    height: 25,
    borderRadius: "50%",
    border: "2px solid #2563eb",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#2563eb",
    fontWeight: 700,
    flexShrink: 0,
  },

  selectedBox: {
    marginTop: 12,
    padding: 12,
    borderRadius: 12,
    background: "#f0fdf4",
    border: "1px solid #bbf7d0",
    display: "flex",
    alignItems: "center",
    gap: 10,
  },

  selectedCheck: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    background: "#16a34a",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
  },

  selectedTitle: {
    fontSize: 11,
    color: "#16a34a",
    fontWeight: 600,
  },

  selectedName: {
    fontSize: 14,
    fontWeight: 700,
    color: "#166534",
    marginTop: 2,
  },

  /* =====================================================
     SUMMARY
  ===================================================== */

  summaryRow: {
  display: "flex",
  justifyContent: "space-between",
  marginBottom: 8,
  color: "#374151",
},

summaryTotal: {
  display: "flex",
  justifyContent: "space-between",
  marginTop: 10,
  paddingTop: 10,
  borderTop: "1px solid #e5e7eb",
  fontSize: 16,
  fontWeight: 800,
  color: "#111827",
},

  checkoutBtn: {
    width: "100%",
    marginTop: 16,
    height: 46,
    borderRadius: 12,
    border: "none",
    background: "#2563eb",
    color: "#ffffff",
    fontSize: 15,
    fontWeight: 600,
    cursor: "pointer",
  },

  empty: {
    height: "100dvh",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    background: "#f9fafb",
  },

  emptyIcon: {
    fontSize: 42,
    marginBottom: 10,
  },

  personCharge: {
  marginTop: 5,
  fontSize: 12,
  fontWeight: 700,
  color: "#92400e",
},

selectedCharge: {
  marginTop: 4,
  fontSize: 13,
  fontWeight: 700,
  color: "#92400e",
},

stockAvailable: {
  marginTop: 4,
  fontSize: 11,
  fontWeight: 600,
  color: "#16a34a",
},

stockLow: {
  marginTop: 4,
  fontSize: 11,
  fontWeight: 700,
  color: "#d97706",
},

stockWarning: {
  marginTop: 4,
  fontSize: 11,
  fontWeight: 700,
  color: "#dc2626",
},

stockOut: {
  marginTop: 4,
  fontSize: 11,
  fontWeight: 700,
  color: "#dc2626",
},

stockLoading: {
  marginTop: 4,
  fontSize: 11,
  color: "#6b7280",
},

surpriseGift: {
  marginTop: 12,
  marginBottom: 10,
  padding: "10px 14px",
  borderRadius: 10,
  background: "#fff7ed",
  border: "1px solid #fed7aa",
  color: "#c2410c",
  fontSize: 13,
  fontWeight: 600,
  textAlign: "center",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 6,
},

/* =====================================================
   DISCOUNT
===================================================== */

discountBtn: {
  width: "100%",
  height: 44,
  marginTop: 12,
  borderRadius: 12,
  border: "1px solid #16a34a",
  background: "#f0fdf4",
  color: "#15803d",
  fontSize: 14,
  fontWeight: 700,
  cursor: "pointer",
},

discountAppliedBox: {
  width: "100%",
  boxSizing: "border-box",
  marginTop: 12,
  padding: "10px 12px",
  borderRadius: 10,
  background: "#f0fdf4",
  border: "1px solid #bbf7d0",
  color: "#15803d",
  fontSize: 13,
  fontWeight: 700,
  textAlign: "center",
},

discountRow: {
  display: "flex",
  justifyContent: "space-between",
  marginBottom: 8,
  color: "#16a34a",
  fontWeight: 600,
},

};